import { Fragment } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { usePatientFacing } from "../../../../flow/patientFacing/hooks/usePatientFacing";
import { buildHannaGeorgeSchedule, hannaStepTranslationKey, isHannaGeorge } from "./hannaGeorgeSchedule";
import { buildConfiguredProtocolSteps } from "./configuredProtocolSteps";

const DateOnly = {
  weekday: "long",
  year: "numeric",
  month: "numeric",
  day: "numeric",
};

const DateAndTime = {
  weekday: "long",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false, // Use 24-hour format
};

const parseProcedureDate = (procedureDate) => {
  if (!procedureDate) return null;
  if (typeof procedureDate === 'string') {
    const datePart = procedureDate.includes('T') ? procedureDate.split('T')[0] : procedureDate;
    return new Date(`${datePart}T00:00:00`);
  }
  return new Date(procedureDate);
};

export function buildInstructionSteps(procedureDate, procedureTime, doctorName, concentrate, instructionProtocol = null, doctorId = null) {
  const prepType = (concentrate || "").split(',')[0].trim().toLowerCase();
  const procedureDateObj = parseProcedureDate(procedureDate);
  const configured = buildConfiguredProtocolSteps(procedureDateObj, procedureTime, instructionProtocol, concentrate, doctorId);
  if (configured) return configured;
  if (isHannaGeorge(doctorName)) {
    const hannaSteps = buildHannaGeorgeSchedule(procedureDateObj, procedureTime, concentrate);
    if (hannaSteps) return hannaSteps;
  }
  const { firstPortion, secondPortion, dietTime, liquids } = getTimes(
    procedureDate,
    procedureTime
  );

  const normalizedDoctorName = (doctorName || "").toLowerCase().replace(/^dr\.?\s*/, "").trim();
  const isPalumboOrMargolis = /palumbo|margolis/i.test(normalizedDoctorName);
  const isNewton = /newton/i.test(normalizedDoctorName);
  const isAckerman = /\b(a[c]?kerman)\b/i.test(normalizedDoctorName);
  const isSepeOrPerera = /\b(sepe|perera)\b/i.test(normalizedDoctorName);
  const isSpecialDoctor = isPalumboOrMargolis || isAckerman || isSepeOrPerera;

  const isSutab = prepType.includes('sutab');
  const isSuprep = prepType.includes('suprep');
  const isGatoradeMiralax = prepType.includes('gatorade') || prepType.includes('miralax');

  let data = [
    { date: dietTime, content: "Diet", options: DateOnly },
    { date: liquids, content: isSpecialDoctor || isNewton ? "SpecialLiquids" : "Liquids", options: DateOnly },
  ];

  if (isSpecialDoctor && (isSutab || isSuprep)) {
    const firstPortionDayBefore = new Date(procedureDateObj);
    firstPortionDayBefore.setDate(procedureDateObj.getDate() - 1);
    firstPortionDayBefore.setHours(17, 0, 0, 0);

    const secondPortionProcedureDay = new Date(procedureDateObj);
    secondPortionProcedureDay.setHours(17, 0, 0, 0);

    data.push(
      { date: firstPortionDayBefore, content: "FirstPortion", options: DateAndTime, timeLabel: "5:00 PM" },
      { date: secondPortionProcedureDay, content: "SecondPortion", options: DateAndTime, timeLabel: "5-8 hours prior to your procedure" }
    );
  } else if (isSpecialDoctor && isGatoradeMiralax) {
    const singlePortionDayBefore = new Date(procedureDateObj);
    singlePortionDayBefore.setDate(procedureDateObj.getDate() - 1);
    singlePortionDayBefore.setHours(17, 0, 0, 0);
    data.push({
      date: singlePortionDayBefore,
      content: "SinglePortion",
      options: DateAndTime,
      timeLabel: "5:00 PM",
    });
  } else if (isNewton && isGatoradeMiralax) {
    const newFirstPortion = new Date(procedureDateObj);
    newFirstPortion.setDate(procedureDateObj.getDate() - 1);
    newFirstPortion.setHours(18, 0, 0, 0);
    const newSecondPortion = new Date(procedureDateObj);
    newSecondPortion.setDate(procedureDateObj.getDate() - 1);
    newSecondPortion.setHours(21, 0, 0, 0);
    data.push(
      { date: newFirstPortion, content: "FirstPortion", options: DateAndTime, timeLabel: "6:00 PM" },
      { date: newSecondPortion, content: "SecondPortion", options: DateAndTime, timeLabel: "9:00 PM" }
    );
  } else if (isNewton) {
    const firstPortionDayBefore = new Date(procedureDateObj);
    firstPortionDayBefore.setDate(procedureDateObj.getDate() - 1);
    firstPortionDayBefore.setHours(17, 0, 0, 0);

    const secondPortionProcedureDay = new Date(procedureDateObj);
    secondPortionProcedureDay.setHours(17, 0, 0, 0);

    data.push(
      { date: firstPortionDayBefore, content: "FirstPortion", options: DateAndTime, timeLabel: "5:00 PM" },
      { date: secondPortionProcedureDay, content: "SecondPortion", options: DateAndTime, timeLabel: "5-8 hours prior to your procedure" }
    );
  } else if (isPalumboOrMargolis) {
    const newFirstPortion = new Date(procedureDateObj);
    newFirstPortion.setHours(17, 0, 0, 0);
    const newSecondPortion = new Date(procedureDateObj);
    newSecondPortion.setHours(17, 0, 0, 0);
    data.push(
      { date: newFirstPortion, content: "FirstPortion", options: DateAndTime, timeLabel: "5:00 PM" },
      { date: newSecondPortion, content: "SecondPortion", options: DateAndTime, timeLabel: "5-8 hours prior to your procedure" }
    );
  } else {
    data.push(
      { date: firstPortion, content: "FirstPortion", options: DateAndTime },
      { date: secondPortion, content: "SecondPortion", options: DateAndTime }
    );
  }

  return data;
}

export default function InstructionsSteps({ keyPrefix = "" }) {
  const { t, i18n } = useTranslation();
  const event = usePatientFacing().getEventDataLocal();
  const Case = event?.Case
  
  const { procedureDate, procedureTime, doctorName, concentrate, instructionProtocol, doctorId } = Case?.GiCase || {};
  const data = buildInstructionSteps(procedureDate, procedureTime, doctorName, concentrate, instructionProtocol, doctorId);
  const language = (i18n.language || "en").toLowerCase();
  const translationLanguage = language.startsWith("pt") ? "pt" : language.startsWith("es") || language.startsWith("sp") ? "es" : null;
  // Use only translations that are actually present; i18next otherwise falls back to English.
  const hannaTranslations = !instructionProtocol && isHannaGeorge(doctorName) && translationLanguage && data.length === 4
    ? data.map(({ content }) => {
      const key = hannaStepTranslationKey(content);
      return key && i18n.getResource?.(translationLanguage, "translation", `${keyPrefix}Instructions-Steps-${key}`);
    })
    : null;
  const hasHannaTranslations = hannaTranslations?.every((text) => typeof text === "string" && text.trim());
  if ((isHannaGeorge(doctorName) || instructionProtocol) && !language.startsWith("en") && !hasHannaTranslations) {
    const notice = language.startsWith("es")
      ? "Estas instrucciones de preparación solo están disponibles en inglés. Pida instrucciones en su idioma a su equipo médico."
      : language.startsWith("pt")
        ? "Estas instruções de preparo estão disponíveis apenas em inglês. Peça instruções em seu idioma à sua equipe médica."
        : "These preparation instructions are currently available only in English. Ask your care team for instructions in your language.";
    return <LanguageNotice role="alert">{notice}</LanguageNotice>;
  }

  // Localized fallbacks for the instruction descriptions
  const fallbackMap = {
    Diet: t(`${keyPrefix}Instructions-Steps-Diet`, "Switch to a low-fiber diet"),
    Liquids: t(`${keyPrefix}Instructions-Steps-Liquids`, "Starting 24 hours before your procedure, start drinking clear fluids only"),
    SpecialLiquids: t(`${keyPrefix}Instructions-Steps-SpecialLiquids`, "24 hours before your procedure, start a clear liquid diet only"),
    FirstPortion: t(`${keyPrefix}Instructions-Steps-FirstPortion`, "Take the first dosage of the laxative solution"),
    SecondPortion: t(`${keyPrefix}Instructions-Steps-SecondPortion`, "Take the second dosage of the laxative solution"),
    SinglePortion: t(`${keyPrefix}Instructions-Steps-SinglePortion`, "Start to take the laxative solution"),
  };

  // Localize fixed time labels returned from buildInstructionSteps
  const localizedTimeLabel = (label) => {
    const map = {
      "5:00 PM": t("Instructions-Steps-Time-5PM", "5:00 PM"),
      "5-8 hours prior to your procedure": t("Instructions-Steps-Time-PreProcedure", "5-8 hours prior to your procedure"),
      "Morning": t("Instructions-Steps-Time-Morning", "Morning"),
      "Noon": t("Instructions-Steps-Time-Noon", "Noon"),
      "Evening": t("Instructions-Steps-Time-Evening", "Evening"),
    };
    return map[label] || label;
  };

  // Helper to format date with friendly time labels
  const formatDateTime = (date, options, timeLabel, exactTime) => {
    if (!date) return "";

    const locale = i18n.language || "en";
    const dateStr = date.toLocaleString(locale, {
      weekday: options.weekday,
      year: options.year,
      month: options.month,
      day: options.day,
    }).replaceAll(",", " |");

    // For DateOnly options, just return formatted date
    if (!options.hour) {
      return dateStr;
    }

    if (exactTime || (hasHannaTranslations && timeLabel === "6 hours prior to your procedure")) {
      return `${dateStr} | ${date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`;
    }

    // If a custom time label is provided, localize it
    if (timeLabel) {
      return `${dateStr} | ${localizedTimeLabel(timeLabel)}`;
    }

    // Fallback to existing friendly time labels
    const hours = date.getHours();
    let fallbackLabel;
    if (hours === 8) {
      fallbackLabel = t("Instructions-Steps-Time-Morning", "Morning");
    } else if (hours === 12) {
      fallbackLabel = t("Instructions-Steps-Time-Noon", "Noon");
    } else if (hours >= 17 || hours === 20) {
      fallbackLabel = t("Instructions-Steps-Time-Evening", "Evening");
    } else {
      fallbackLabel = date.toLocaleString(locale, {
        hour: options.hour,
        minute: options.minute,
        hour12: options.hour12,
      });
    }

    return `${dateStr} | ${fallbackLabel}`;
  };

  return (
    <Container>
      {data.map(({ date, content, options, timeLabel, description, exactTime }, index) => (
        <Fragment key={content}>
          <Bullet />
          <StepDetails>
            {formatDateTime(date, options, timeLabel, exactTime)}
          </StepDetails>
          <DottedLine $last={index === data.length - 1} />
          <DescriptionText>
            {hasHannaTranslations ? hannaTranslations[index] : description || t(`${keyPrefix}Instructions-Steps-${content}`, fallbackMap[content])}
          </DescriptionText>
        </Fragment>
      ))}
    </Container>
  );
}

export const getTimes = (procedureDate, procedureTime) => {
  // Parse date as local time to avoid UTC timezone offset issues
  // Handle both date-only strings ("2026-06-03") and ISO strings ("2026-06-03T00:00:00.000Z")
  // Always extract just the date part and treat as local time
  let date;
  if (typeof procedureDate === 'string') {
    // Extract date part before 'T' if present, then force local timezone
    const datePart = procedureDate.includes('T') ? procedureDate.split('T')[0] : procedureDate;
    date = new Date(`${datePart}T00:00:00`);
  } else {
    date = new Date(procedureDate);
  }
  const [hour] = (procedureTime || "08:00").split(":").map(Number);
  
  console.log('DEBUG getTimes:', { procedureDate, procedureTime, hour, date: date.toISOString() });

  // T1: 2 days before procedure (diet change)
  const dietTime = new Date(date);
  dietTime.setDate(date.getDate() - 2);

  // T2: 24 hours (1 day) before procedure (clear fluids only)
  const liquids = new Date(date);
  liquids.setDate(date.getDate() - 1);

  const firstPortion = new Date(date);
  const secondPortion = new Date(date);

  const isMorning = hour >= 7 && hour <= 11;   // morning: 07:00–11:59
  const isNoon    = hour >= 12 && hour <= 17;  // noon:    12:00–17:59 (includes 17:00)
  const isEvening = hour >= 18;                // evening: 18:00+

  if (isMorning) {
    // T3: day before + noon (12:00)
    firstPortion.setDate(date.getDate() - 1);
    firstPortion.setHours(12, 0, 0, 0);
    // T4: day before + evening (20:00)
    secondPortion.setDate(date.getDate() - 1);
    secondPortion.setHours(20, 0, 0, 0);
  } else if (isNoon) {
    // T3: day before + evening (20:00)
    firstPortion.setDate(date.getDate() - 1);
    firstPortion.setHours(20, 0, 0, 0);
    // T4: day of procedure + morning (08:00)
    secondPortion.setHours(8, 0, 0, 0);
  } else if (isEvening) {
    // T3: day of procedure + morning (08:00)
    firstPortion.setHours(8, 0, 0, 0);
    // T4: day of procedure + noon (12:00)
    secondPortion.setHours(12, 0, 0, 0);
  } else {
    // Fallback for early morning (< 7:00) — treat as morning
    firstPortion.setDate(date.getDate() - 1);
    firstPortion.setHours(12, 0, 0, 0);
    secondPortion.setDate(date.getDate() - 1);
    secondPortion.setHours(20, 0, 0, 0);
  }
  const result = { firstPortion, secondPortion, dietTime, liquids };
  console.log('DEBUG getTimes result:', {
    firstPortion: firstPortion.toISOString(),
    secondPortion: secondPortion.toISOString(),
    isMorning: hour >= 7 && hour <= 11,
    isNoon: hour >= 12 && hour <= 17,
    isEvening: hour >= 18,
  });
  return result;
};

const DottedLine = styled.div`
  height: 100%;
  border-right: 1px dotted #7a9dfd;
  visibility: ${({ $last }) => ($last ? "hidden" : "visible")};
`;

const LanguageNotice = styled.p`
  color: #753c00;
  background: #fff7e8;
  border-left: 3px solid #ad6500;
  padding: 12px;
`;

const Container = styled.div`
  margin-block-end: 5px;
  display: grid;
  grid-template-columns: 15px auto;
  grid-template-rows: repeat(8, min-content);
  place-items: center;
  column-gap: 8px;
`;

const Bullet = styled.div`
  background-color: #527ffa;
  width: 0.688rem;
  height: 0.688rem;
  border-radius: 50%;
`;

const StepDetails = styled.div`
  color: #527ffa;
  font-size: 1.188rem;
  place-self: start;
`;

const DescriptionText = styled.div`
  place-self: start;
  font-size: 1.188rem;
  padding-block-end: 1.063rem;
`;
